import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { mergeExistingFields } from "helpers/mergeExistingFields";
import sanitizeFileName from "helpers/sanitizeFileName";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export type BrandCoreColumn = string;

const FOOTER_BRANDCORE_COLUMNS: BrandCoreColumn[] = [
  "pages",
  "promos",
  "games",
];
export interface BrandCore {
  id: number;
  title: string;
  geo: string;
  isActive: number;
}

export type BrandCoreExtended = BrandCore & {
  logo: string;
  columns: BrandCoreColumn[];
};

export type BrandCoreDraftItem = BrandCoreExtended & {
  isModified: boolean;
  logoRef: string;
};

export interface BrandCoreState {
  list: BrandCore[];
  isListFetched: boolean;
  isItemFetched: boolean;
  isItemFetching: boolean;
  selectedItems: BrandCore["id"][];
  processingItemId?: BrandCore["id"];
  activePage: number;
  totalItems: number;
  totalPages: number;
  draftItem: BrandCoreDraftItem;
}

interface getBrandCoreListResponse {
  items: BrandCore[];
  totalItems: number;
  totalPages: number;
}

interface getBrandCoreListPayload {
  limit?: number;
  offset?: number;
  title?: string;
  countries?: string[];
  isActive?: number;
  order?: Partial<Record<keyof BrandCore, string>>;
}

interface UpdateBrandCoreItemPayload extends Omit<BrandCore, "geo"> {
  logo?: File;
  country: string;
  columns: string[];
}

export type UpdateFormDataPayload = Omit<
  BrandCoreDraftItem,
  "logo" | "isModified" | "logoRef"
> & { logo?: File };

interface BrandCoreBunchUpdatePayload {
  ids: number[];
  isActive: number;
}

const bunchUpdateBrandCore = createAsyncThunk(
  "cms-brandCore/bunch-update",
  async (data: BrandCoreBunchUpdatePayload) => {
    try {
      const result = await instance.put(
        `${apiRoutes.brandCoreBunchUpdate}`,
        data
      );

      return result.data;
    } catch (error) {
      throw error;
    }
  }
);
const createDataForUpdate = (
  data: Partial<Omit<UpdateFormDataPayload, "id">>
) => {
  const formData = new FormData();

  Object.keys(data).forEach((k) => {
    const key = k as keyof Omit<
      BrandCoreDraftItem,
      "id" | "isModified" | "logoRef"
    >;

    switch (key) {
      case "geo": {
        formData.append("country", `${data[key]}`);
        return;
      }

      case "columns": {
        const columns = data[key];

        if (!columns || !columns.length) return;

        columns.forEach((col) => {
          formData.append("columns[]", col);
        });
        return;
      }

      case "logo": {
        if (data[key] && data[key] instanceof File) {
          const logo = data[key] as File;

          const safeName = sanitizeFileName(logo.name);

          formData.append(
            "logo",
            new File([logo], safeName, { type: logo.type })
          );
        }

        return;
      }

      default: {
        formData.append(key, `${data[key]}`);
      }
    }
  });

  return formData;
};

const getBrandCoreList = createAsyncThunk(
  "cms-brandCore/list",
  async (data: getBrandCoreListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.brandCoreList}`, data);
      return result.data as getBrandCoreListResponse;
    } catch (error) {
      throw error;
    }
  }
);

const deleteBrandCoreItem = createAsyncThunk(
  "cms-brandCore/delete",
  async (id: number) => {
    try {
      await instance.delete(`${apiRoutes.brandCoreDelete}/${id}`);
      return true;
    } catch (error) {
      throw error;
    }
  }
);

const getBrandCoreItem = createAsyncThunk(
  "cms-brandCore/item",
  async (id: number) => {
    try {
      const result = await instance.get(`${apiRoutes.brandCoreItem}/${id}`);
      return result.data as BrandCoreExtended;
    } catch (error) {
      throw error;
    }
  }
);



const createBrandCoreItem = createAsyncThunk<
  BrandCoreExtended,
  Omit<UpdateFormDataPayload, "id">,
  { rejectValue: ApiErrorPayload }
>("cms-brandCore/create", async (data, { rejectWithValue }) => {
  try {
    const formData = createDataForUpdate(data);

    const result = await instance.post(
      `${apiRoutes.brandCoreCreate}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return result.data as BrandCoreExtended;
  } catch (error: any) {
    const apiError = error?.data?.error;
    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});
const updateBrandCoreItem = createAsyncThunk<
  BrandCoreExtended,
  UpdateFormDataPayload,
  { rejectValue: ApiErrorPayload }
>("cms-brandCore/update", async (data, { rejectWithValue }) => {
  try {
    const { id, ...restData } = data;
    const formData = createDataForUpdate(restData);
    const result = await instance.put(
      `${apiRoutes.brandCoreUpdate}/${id}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return result.data as BrandCoreExtended;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError && apiError.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

const createDraftItem = (data?: BrandCoreExtended): BrandCoreDraftItem => {
  if (!data) {
    return {
      id: 0,
      title: "",
      geo: "",
      isActive: 0,
      logo: "",
      logoRef: "",
      columns: [],
      isModified: false,
    };
  }

  return {
    id: data.id,
    title: data.title,
    geo: data.geo,
    isActive: data.isActive,
    logo: "",
    logoRef: data?.logo || "",
    columns: data.columns,
    isModified: false,
  };
};

const initialState: BrandCoreState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  isItemFetching: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 1,
  selectedItems: [],
  draftItem: createDraftItem(),
};

const brandCoreSlice = createSlice({
  name: "cms-brandCore",
  initialState,
  reducers: {
    handleSelect: (state, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload
        );
      } else {
        state.selectedItems.push(action.payload);
      }
    },
    handleSelectAll: (state) => {
      state.selectedItems =
        state.selectedItems.length !== state.list.length
          ? state.list.map((item) => item.id)
          : [];
    },
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleClearDraftItem: (state) => {
      state.isItemFetched = false;
      state.draftItem = createDraftItem();
    },
    handleUpdateDraftItem: (
      state,
      action: PayloadAction<Partial<BrandCoreDraftItem>>
    ) => {
      if (action.payload.logo === "") {
        state.draftItem = {
          ...state.draftItem,
          isModified: true,
        };

        return;
      }

      state.draftItem = {
        ...state.draftItem,
        ...action.payload,
        isModified: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getBrandCoreList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(deleteBrandCoreItem.pending, (state, action) => {
      state.processingItemId = action.meta.arg;
    });
    builder.addCase(deleteBrandCoreItem.fulfilled, (state) => {
      state.processingItemId = undefined;
      state.isListFetched = false;
    });
    builder.addCase(deleteBrandCoreItem.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(getBrandCoreItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.draftItem = createDraftItem(action.payload);
    });
    builder.addCase(updateBrandCoreItem.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateBrandCoreItem.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.list = state.list.map((item) =>
        item.id === action.payload.id
          ? mergeExistingFields(item, action.payload)
          : item,
      );
      state.processingItemId = undefined;
      state.draftItem = createDraftItem(action.payload);
    });
    builder.addCase(updateBrandCoreItem.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(createBrandCoreItem.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createBrandCoreItem.fulfilled, (state) => {
      state.isItemFetching = false;
      state.isListFetched = false;
    });
    builder.addCase(createBrandCoreItem.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(bunchUpdateBrandCore.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
  },
});

const {
  handleSelect,
  handleSelectAll,
  handlePaginate,
  handleClearDraftItem,
  handleUpdateDraftItem,
} = brandCoreSlice.actions;

export {
  brandCoreSlice,
  handleSelect,
  handleSelectAll,
  handlePaginate,
  getBrandCoreList,
  deleteBrandCoreItem,
  updateBrandCoreItem,
  getBrandCoreItem,
  createBrandCoreItem,
  handleClearDraftItem,
  handleUpdateDraftItem,
  bunchUpdateBrandCore,
  FOOTER_BRANDCORE_COLUMNS,
};
